<template>
  <q-fila label="Foto del Acta de Escrutinio" :completa="hayFotos" :resaltar="true">
    <div v-if="rutas.length" class="d-flex flex-wrap mb-3">
      <div v-for="ruta in rutas" :key="ruta" class="ficha-acta-thumb mr-3 mb-3">
        <a :href="urlFoto(ruta)" target="_blank" class="ficha-acta-thumb__link">
          <img :src="urlFoto(ruta)" alt="Acta de escrutinio" />
        </a>
        <button v-if="!soloLectura" type="button" class="ficha-acta-thumb__quitar"
                title="Eliminar" @click.prevent="quitar(ruta)">
          <i class="fa fa-trash"></i>
        </button>
      </div>
    </div>

    <div v-if="!soloLectura" class="alert alert-custom alert-light-info py-2 mb-3 px-5 align-items-center">
      <i class="fa fa-info-circle mr-3 text-info"></i>
      <div style="border-left: 1px solid #c0a1ff; padding-left: 12px;">
        Asegúrate que en la fotografía se encuentre <b>toda la información del Acta de Escrutinio</b> claramente visible.
      </div>
    </div>

    <file-pond
      v-if="!soloLectura"
      class="ficha-acta-pond"
      ref="pond"
      name="actaEscrutinio"
      :allow-multiple="true"
      :allow-revert="true"
      :instant-upload="false"
      accepted-file-types="image/jpeg, image/png, image/gif, image/jpg"
      label-idle="Arrastra fotos del acta o <u>haz clic aquí</u><br><i class='fa fa-image fa-2x'></i>"
      @addfile="onAddFile"
    />
    <small v-if="!hayFotos" class="text-muted d-block mt-1">
      Sube una o más fotos donde se vea completa la información del acta.
    </small>
  </q-fila>
</template>

<style>
.ficha-acta-thumb {
  position: relative;
  display: inline-block;
}

.ficha-acta-thumb__link {
  display: block;
}

.ficha-acta-thumb img {
  height: 140px;
  width: auto;
  max-width: 220px;
  object-fit: contain;
  background: #fff;
  border: 1px solid #e4e6ef;
  border-radius: 4px;
}

.ficha-acta-thumb__quitar {
  position: absolute;
  top: 6px;
  right: 6px;
  z-index: 2;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.92);
  color: #f64e60;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  cursor: pointer;
}

.ficha-acta-thumb__quitar:hover {
  color: #f64e60;
  background: #fff;
}

.ficha-acta-thumb__quitar {
  color: #858585;
  background: #fff;
}


.ficha-acta-thumb__quitar i {
  color: inherit;
}

.ficha-acta-pond.filepond--wrapper {
  border: 2px dashed #b5b5c3;
  border-radius: 8px;
}

.ficha-acta-pond .filepond--root {
  margin-bottom: 0;
}

.ficha-acta-pond .filepond--credits {
  display: none;
}



.ficha-acta-pond .filepond--drop-label {
  font-size: 1.15rem;
}

.ficha-acta-pond .filepond--panel-root {
  background-color: #eefaff;
}

.ficha-acta-pond .filepond--label-action {
  font-size: inherit;
}

.ficha-acta-pond .filepond--file-info,
.ficha-acta-pond .filepond--file-status {
  font-size: 0.95rem;
}
</style>

<script>
/**
 * Carga de fotos del acta de escrutinio (FilePond).
 * FilePond solo recibe archivos nuevos: las ya guardadas se muestran con <img>
 * porque el plugin intenta recargarlas por XHR y las estáticas del API no
 * responden a ese pedido (la URL sí existe y se ve en el navegador).
 */
import vueFilePond from "vue-filepond";
import "filepond/dist/filepond.min.css";
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";
import FilePondPluginImagePreview from "filepond-plugin-image-preview";
import QFila from "./ui/QFila";

const FilePond = vueFilePond(FilePondPluginFileValidateType, FilePondPluginImagePreview);
const ORIGEN_LOCAL = 3;
const SEPARADOR = "|";

export default {
  name: "FichaActaEscrutinio",
  components: { FilePond, QFila },
  props: {
    value: { type: String, default: null },
    controlador: { type: String, required: true },
    localVotacionMesaProcesoElectoralId: { type: [Number, String], default: null },
    mesaVotacionId: { type: [Number, String], required: true },
    procesoElectoralId: { type: [Number, String], required: true },
    soloLectura: { type: Boolean, default: false },
  },
  computed: {
    rutas() {
      return this.listaRutas(this.value);
    },
    hayFotos() {
      return this.rutas.length > 0;
    },
  },
  methods: {
    listaRutas(valor) {
      return String(valor || "")
        .split(SEPARADOR)
        .map((x) => x.trim())
        .filter((x) => x.length > 0);
    },
    urlFoto(ruta) {
      return this.apiResource(ruta);
    },
    identificacionFormData() {
      const fd = new FormData();
      const id = this.localVotacionMesaProcesoElectoralId;
      if (id) fd.append("LocalVotacionMesaProcesoElectoralId", id);
      fd.append("MesaVotacionId", this.mesaVotacionId);
      fd.append("ProcesoElectoralId", this.procesoElectoralId);
      return fd;
    },
    emitir(ruta, datos) {
      this.$emit("input", ruta || null);
      if (datos) this.$emit("guardado", datos);
    },
    sacarDePond(fileItem) {
      if (!this.$refs.pond || !fileItem) return;
      this.$refs.pond.removeFile(fileItem.id);
    },
    onAddFile(error, fileItem) {
      if (error || !fileItem || this.soloLectura) return;
      if (fileItem.origin === ORIGEN_LOCAL) return;
      const fd = this.identificacionFormData();
      fd.append("Imagenes", fileItem.file);
      this.$http
        .post(this.apiRoute(this.controlador, "SubirActaEscrutinio"), fd)
        .then(function (response) {
          this.sacarDePond(fileItem);
          if (this.procesarRespuestaErrorDefecto(response)) return;
          const data = response.body.data || {};
          this.emitir((data.ficha || {}).rutaActaEscrutinio || null, data);
        }, function (err) {
          this.sacarDePond(fileItem);
          this.mostrarMensajeExcepcionVueResource(err);
        });
    },
    quitar(ruta) {
      this.$bvModal
        .msgBoxConfirm("¿Eliminar esta foto del acta de escrutinio?", {
          title: "Confirmar",
          okTitle: "Eliminar",
          cancelTitle: "Cancelar",
          okVariant: "danger",
          centered: true,
        })
        .then((confirmado) => {
          if (!confirmado) return;
          const fd = this.identificacionFormData();
          fd.append("EsEliminado", true);
          fd.append("RutaEliminar", ruta);
          this.$http
            .post(this.apiRoute(this.controlador, "SubirActaEscrutinio"), fd)
            .then(function (response) {
              if (this.procesarRespuestaErrorDefecto(response)) return;
              const data = response.body.data || {};
              this.emitir((data.ficha || {}).rutaActaEscrutinio || null, data);
            }, this.mostrarMensajeExcepcionVueResource);
        });
    },
  },
};
</script>
